import React, { useState, useEffect, useRef, useCallback } from 'react';
import { socket } from '../navegacao/socket';
import { api } from '../../lib/apiCall';
import { useAuth } from '../../lib/useAuth';
import { toast } from 'react-toastify';

export interface ChatInternoParticipante {
  id: number;
  chat_interno_id: number;
  usuario_id: number;
  usuario?: {
    id: number;
    nome: string;
    email?: string;
  };
  data_entrada: string;
  data_ultima_leitura?: string;
}

export interface ChatInternoMensagem {
  id: number;
  chat_interno_id: number;
  autor_id: number;
  autor?: {
    id: number;
    nome: string;
  };
  conteudo: string;
  mencoes?: number[];
  data_criacao: string;
}

export interface ChatInternoConversa {
  id: number;
  titulo?: string;
  criador_id: number;
  criador?: {
    id: number;
    nome: string;
  };
  participantes?: ChatInternoParticipante[];
  ultima_mensagem?: ChatInternoMensagem;
  nao_lidas?: number;
  data_criacao: string;
  data_ultima_atualizacao: string;
}

export interface UsuarioMention {
  id: string | number;
  nome: string;
  avatarUrl?: string;
  apelido?: string;
  grupos?: { id: number; nome: string }[];
}

export interface UseChatInternoReturn {
  conversas: ChatInternoConversa[];
  conversaSelecionada: ChatInternoConversa | null;
  mensagens: ChatInternoMensagem[];
  loading: boolean;
  loadingMensagens: boolean;
  totalNaoLidas: number;
  usuariosDigitando: Map<number, { usuario: { id: number; nome: string }; chatId: number }>;
  usuarios: UsuarioMention[];
  carregarConversas: () => Promise<void>;
  selecionarConversa: (conversa: ChatInternoConversa) => void;
  enviarMensagem: (conteudo: string) => void;
  criarConversa: (participanteIds: number[], titulo?: string) => Promise<ChatInternoConversa | null>;
  adicionarParticipante: (usuarioId: number) => void;
  setDigitando: (digitando: boolean) => void;
  fecharConversa: () => void;
  carregarUsuarios: () => Promise<void>;
  carregarMaisMensagens: () => Promise<void>;
  temMaisMensagens: boolean;
}

export function useChatInterno(): UseChatInternoReturn {
  const { user } = useAuth();
  const [conversas, setConversas] = useState<ChatInternoConversa[]>([]);
  const [conversaSelecionada, setConversaSelecionada] = useState<ChatInternoConversa | null>(null);
  const [mensagens, setMensagens] = useState<ChatInternoMensagem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMensagens, setLoadingMensagens] = useState(false);
  const [usuariosDigitando, setUsuariosDigitando] = useState<Map<number, { usuario: { id: number; nome: string }; chatId: number }>>(new Map());
  const [usuarios, setUsuarios] = useState<UsuarioMention[]>([]);
  const [temMaisMensagens, setTemMaisMensagens] = useState(true);

  const digitandoTimeoutRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
  const cursorRef = useRef<number | undefined>(undefined);
  const conversasRef = useRef<ChatInternoConversa[]>([]);
  const conversaSelecionadaRef = useRef<ChatInternoConversa | null>(null);

  // Keep refs updated
  useEffect(() => {
    conversasRef.current = conversas;
  }, [conversas]);

  useEffect(() => {
    conversaSelecionadaRef.current = conversaSelecionada;
  }, [conversaSelecionada]);

  const totalNaoLidas = conversas.reduce((acc, c) => acc + (c.nao_lidas ?? 0), 0);

  // Carrega lista de conversas
  const carregarConversas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/chat_interno');
      if (response.data.ok) {
        setConversas(response.data.result);
      }
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Carrega lista de usuários para menções
  const carregarUsuarios = useCallback(async () => {
    try {
      const response = await api.get('/api/usuario/box');
      if (response.data.result) {
        setUsuarios(
          response.data.result.map((u: any) => ({
            id: u.id,
            nome: u.nome,
            grupos: u.grupos || [],
          }))
        );
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  }, []);

  // Seleciona uma conversa e carrega mensagens
  const selecionarConversa = useCallback(async (conversa: ChatInternoConversa) => {
    setConversaSelecionada(conversa);
    setMensagens([]);
    setLoadingMensagens(true);
    cursorRef.current = undefined;
    setTemMaisMensagens(true);

    try {
      const response = await api.get(`/api/chat_interno/${conversa.id}/mensagens`);
      if (response.data.ok) {
        const msgs = response.data.result;
        setMensagens(msgs.reverse());
        if (msgs.length > 0) {
          cursorRef.current = msgs[0].id;
        }
        if (msgs.length < 50) {
          setTemMaisMensagens(false);
        }
      }

      // Marca como lido
      socket.emit('chat_interno:marcar_lido', { chat_interno_id: conversa.id });

      // Atualiza contagem de não lidas
      setConversas(prev => prev.map(c =>
        c.id === conversa.id ? { ...c, nao_lidas: 0 } : c
      ));
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    } finally {
      setLoadingMensagens(false);
    }
  }, []);

  // Carrega mais mensagens (paginação)
  const carregarMaisMensagens = useCallback(async () => {
    if (!conversaSelecionada || !cursorRef.current || loadingMensagens || !temMaisMensagens) return;

    setLoadingMensagens(true);
    try {
      const response = await api.get(`/api/chat_interno/${conversaSelecionada.id}/mensagens?cursor=${cursorRef.current}`);
      if (response.data.ok) {
        const msgs = response.data.result;
        if (msgs.length > 0) {
          setMensagens(prev => [...msgs.reverse(), ...prev]);
          cursorRef.current = msgs[0].id;
        }
        if (msgs.length < 50) {
          setTemMaisMensagens(false);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar mais mensagens:', error);
    } finally {
      setLoadingMensagens(false);
    }
  }, [conversaSelecionada, loadingMensagens, temMaisMensagens]);

  // Envia uma mensagem
  const enviarMensagem = useCallback((conteudo: string) => {
    if (!conversaSelecionada || !conteudo.trim()) return;

    socket.emit('chat_interno:enviar_mensagem', {
      chat_interno_id: conversaSelecionada.id,
      conteudo,
    });
  }, [conversaSelecionada]);

  // Cria uma nova conversa
  const criarConversa = useCallback(async (participanteIds: number[], titulo?: string): Promise<ChatInternoConversa | null> => {
    try {
      const response = await api.post('/api/chat_interno', {
        participante_ids: participanteIds,
        titulo,
      });

      if (response.data.ok) {
        const novaConversa = response.data.result;
        setConversas(prev => [novaConversa, ...prev]);
        return novaConversa;
      }
    } catch (error) {
      console.error('Erro ao criar conversa:', error);
      toast.error('Erro ao criar conversa');
    }
    return null;
  }, []);

  // Adiciona um participante à conversa atual
  const adicionarParticipante = useCallback((usuarioId: number) => {
    if (!conversaSelecionada) return;

    socket.emit('chat_interno:adicionar_participante', {
      chat_interno_id: conversaSelecionada.id,
      usuario_id: usuarioId,
    });
  }, [conversaSelecionada]);

  // Indica status de digitação
  const setDigitando = useCallback((digitando: boolean) => {
    if (!conversaSelecionada) return;

    socket.emit('chat_interno:digitando', {
      chat_interno_id: conversaSelecionada.id,
      digitando,
    });
  }, [conversaSelecionada]);

  // Fecha a conversa selecionada
  const fecharConversa = useCallback(() => {
    setConversaSelecionada(null);
    setMensagens([]);
    cursorRef.current = undefined;
  }, []);

  // Handlers de eventos do socket
  useEffect(() => {
    const handleNovaMensagem = (data: { chat_interno_id: number; mensagem: ChatInternoMensagem }) => {
      // Adiciona mensagem à conversa selecionada
      if (conversaSelecionadaRef.current?.id === data.chat_interno_id) {
        setMensagens(prev => [...prev, data.mensagem]);

        // Marca como lido se a conversa está aberta
        socket.emit('chat_interno:marcar_lido', { chat_interno_id: data.chat_interno_id });
      } else {
        // Atualiza contador de não lidas
        setConversas(prev => prev.map(c =>
          c.id === data.chat_interno_id
            ? { ...c, nao_lidas: (c.nao_lidas ?? 0) + 1, ultima_mensagem: data.mensagem }
            : c
        ));
      }

      // Move conversa para o topo
      setConversas(prev => {
        const conversaIndex = prev.findIndex(c => c.id === data.chat_interno_id);
        if (conversaIndex > 0) {
          const conversa = prev[conversaIndex];
          const novaLista = [...prev];
          novaLista.splice(conversaIndex, 1);
          return [{ ...conversa, ultima_mensagem: data.mensagem }, ...novaLista];
        }
        return prev;
      });
    };

    const handleMencao = (data: { chat_interno_id: number; mensagem: ChatInternoMensagem; autor: { id: number; nome: string } }) => {
      // Notifica o usuário sobre a menção
      if (data.autor.id !== user?.id) {
        toast.info(`${data.autor.nome} mencionou você em uma conversa`, {
          toastId: `mencao-${data.mensagem.id}`,
          position: 'top-right',
          autoClose: 5000,
          onClick: () => {
            // Abre a conversa quando clica na notificação
            const conversa = conversasRef.current.find(c => c.id === data.chat_interno_id);
            if (conversa) {
              selecionarConversa(conversa);
            }
          },
        });

        // Toca som de notificação
        new Audio('/sounds/novo_ticket.mp3').play().catch(() => {
          console.warn('Erro ao tocar som de notificação');
        });
      }
    };

    const handleDigitando = (data: { chat_interno_id: number; usuario: { id: number; nome: string }; digitando: boolean }) => {
      if (data.usuario.id === user?.id) return;

      setUsuariosDigitando(prev => {
        const novo = new Map(prev);
        if (data.digitando) {
          novo.set(data.usuario.id, { usuario: data.usuario, chatId: data.chat_interno_id });

          // Remove status de digitação após 3 segundos
          const existingTimeout = digitandoTimeoutRef.current.get(data.usuario.id);
          if (existingTimeout) {
            clearTimeout(existingTimeout);
          }
          const timeout = setTimeout(() => {
            setUsuariosDigitando(p => {
              const n = new Map(p);
              n.delete(data.usuario.id);
              return n;
            });
          }, 3000);
          digitandoTimeoutRef.current.set(data.usuario.id, timeout);
        } else {
          novo.delete(data.usuario.id);
        }
        return novo;
      });
    };

    const handleNovaConversa = (data: { conversa: ChatInternoConversa }) => {
      setConversas(prev => {
        // Evita duplicatas
        if (prev.some(c => c.id === data.conversa.id)) return prev;
        return [data.conversa, ...prev];
      });
    };

    const handleParticipanteAdicionado = (data: { chat_interno_id: number; usuario_id: number; conversa: ChatInternoConversa }) => {
      setConversas(prev => prev.map(c =>
        c.id === data.chat_interno_id ? data.conversa : c
      ));

      if (conversaSelecionadaRef.current?.id === data.chat_interno_id) {
        setConversaSelecionada(data.conversa);
      }
    };

    const handleErro = (data: { message: string }) => {
      toast.error(data.message);
    };

    socket.on('chat_interno:nova_mensagem', handleNovaMensagem);
    socket.on('chat_interno:mencao', handleMencao);
    socket.on('chat_interno:digitando', handleDigitando);
    socket.on('chat_interno:nova_conversa', handleNovaConversa);
    socket.on('chat_interno:participante_adicionado', handleParticipanteAdicionado);
    socket.on('chat_interno:erro', handleErro);

    return () => {
      socket.off('chat_interno:nova_mensagem', handleNovaMensagem);
      socket.off('chat_interno:mencao', handleMencao);
      socket.off('chat_interno:digitando', handleDigitando);
      socket.off('chat_interno:nova_conversa', handleNovaConversa);
      socket.off('chat_interno:participante_adicionado', handleParticipanteAdicionado);
      socket.off('chat_interno:erro', handleErro);

      // Limpa timeouts
      digitandoTimeoutRef.current.forEach(timeout => clearTimeout(timeout));
    };
  }, [user, selecionarConversa]);

  // Carrega conversas e usuários ao montar (somente quando usuário está autenticado)
  useEffect(() => {
    if (user?.id) {
      carregarConversas();
      carregarUsuarios();
    }
  }, [user?.id, carregarConversas, carregarUsuarios]);

  return {
    conversas,
    conversaSelecionada,
    mensagens,
    loading,
    loadingMensagens,
    totalNaoLidas,
    usuariosDigitando,
    usuarios,
    carregarConversas,
    selecionarConversa,
    enviarMensagem,
    criarConversa,
    adicionarParticipante,
    setDigitando,
    fecharConversa,
    carregarUsuarios,
    carregarMaisMensagens,
    temMaisMensagens,
  };
}
